import { SaveOutlined, MoreOutlined } from "@ant-design/icons";
import { Template } from "../VideoEditor";
import { templateInputConfigDemo } from "../../remotion/Root";
import { useEffect, useState } from "react";
import TrebbleClientAPIHelper from "../../../../models/helper/TrebbleClientAPI";
import TrebbleDropdown from "../../../common/TrebbleDropdown";
import TrebbleButton from "../../../common/TrebbleButton";
import TrebbleSelect from "../../../common/TrebbleSelect";
import TrebbleModal from "../../../common/TrebbleModal";
import TrebbleForm from "../../../common/TrebbleForm";
import TrebbleInput from "../../../common/TrebbleInput";
import TrebbleMessage from "../../../common/TrebbleMessage";
import ti18n from 'i18n!nls/ClipCreatori18n'; // Import i18n

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

export const createDefaultTemplate = (): Template => ({
    ...templateInputConfigDemo,
    templateID: '1',
    type: "trebble" as const,
    name: window.getI18n(ti18n, 'DEFAULT_TEMPLATE_NAME'), // Use i18n key
    showCaption: true,
    overrideThemeCaption: false
  });

export const SaveTemplateButton = ({
    onSaveMenuClicked,
    onSaveAsMenuClicked,
    isTrebbleTemplate,
    className,
    isSaving,
    isConfigEditing,
}: { 
    onSaveMenuClicked: () => void, 
    onSaveAsMenuClicked: () => void, 
    isTrebbleTemplate: boolean,
    className: string,
    isSaving: boolean,
    isConfigEditing: boolean,
}) => {
    const isDisabled = isSaving || isConfigEditing;

    const items = [
        {
            key: "1",
            label: (
                <a onClick={!isDisabled && !isTrebbleTemplate ? onSaveMenuClicked : undefined} style={{
                    pointerEvents: (isDisabled || isTrebbleTemplate) ? 'none' : 'auto',
                    cursor: (isDisabled || isTrebbleTemplate) ? 'not-allowed' : 'pointer',
                    color: (isDisabled || isTrebbleTemplate) ? 'rgba(0, 0, 0, 0.25)' : 'inherit'
                }}>
                    {window.getI18n(ti18n, 'SAVE')}
                </a>
            ),
            disabled: isDisabled || isTrebbleTemplate,
        },
        {
            key: "2",
            label: (
                <a onClick={!isDisabled ? onSaveAsMenuClicked : undefined} style={{
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : 'inherit'
                }}>
                    {window.getI18n(ti18n, 'SAVE_AS')}
                </a>
            ),
            disabled: isDisabled,
        },
    ];
    return (
        <TrebbleDropdown
            trigger={["click"]}
            className={className}
            menu={{
                items,
            }}
            placement="bottom"
            disabled={isDisabled}
        >
            <TrebbleButton icon={<SaveOutlined />} disabled={isDisabled} style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
        </TrebbleDropdown>
    );
};

export const MoreActionTemplateButton = ({
    onResetMenuClicked,
    onRenameMenuClicked,
    onDeleteMenuClicked,
    isTrebbleTemplate,
    className,
    isSaving,
    isConfigEditing,
}: { 
    onResetMenuClicked: () => void, 
    onRenameMenuClicked?: () => void, 
    onDeleteMenuClicked?: () => void, 
    isTrebbleTemplate: boolean,
    className?: string,
    isSaving: boolean,
    isConfigEditing: boolean,
}) => {
    const isDisabled = isSaving || isConfigEditing;

    const items = [
        {
            key: "1",
            label: (
                <a onClick={!isDisabled ? onResetMenuClicked : undefined} style={{
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : 'inherit'
                }}>
                    {window.getI18n(ti18n, 'RESET')}
                </a>
            ),
            disabled: isDisabled,
        },
        {
            key: "3",
            label: (
                <a
                    onClick={(e) => {
                        if (!isTrebbleTemplate && !isDisabled) {
                            onDeleteMenuClicked?.();
                        }
                    }}
                    style={{ 
                        color: isTrebbleTemplate ? "rgba(0, 0, 0, 0.25)" : "red", 
                        pointerEvents: isDisabled ? 'none' : 'auto',
                        cursor: (isDisabled || isTrebbleTemplate) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {window.getI18n(ti18n, 'DELETE')}
                </a>
            ),
            disabled: isDisabled || isTrebbleTemplate,
        }
    ];
    return (
        <TrebbleDropdown
            trigger={["click"]}
            className={className}
            menu={{
                items,
            }}
            placement="bottom"
            disabled={isDisabled}
        >
            <TrebbleButton icon={<MoreOutlined />} disabled={isDisabled} style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
        </TrebbleDropdown>
    );
};

export const TemplateSelector = ({ 
    savedTemplates, 
    setSavedTemplates, 
    selectedTemplate, 
    setSelectedTemplate,
    isConfigEditing,
    workspaceId
}: {
    savedTemplates: Template[],
    setSavedTemplates: React.Dispatch<React.SetStateAction<Template[]>>,
    selectedTemplate: Template,
    setSelectedTemplate: (template: Template) => void,
    workspaceId: string | null | undefined,
    isConfigEditing: boolean,
}) => {
    const [isSaveAsModalVisible, setIsSaveAsModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [form] = TrebbleForm.useForm();
    const [isSavingAs, setIsSavingAs] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);

    const checkIfTemplateChanged = (inMemoryTemplate: Template, savedTemplates: Template[]) => {
        const templateFromStorage = savedTemplates.find((t) => t.templateID === inMemoryTemplate.templateID);
        if (templateFromStorage === undefined) {
            return false;
        }
        return JSON.stringify(inMemoryTemplate) !== JSON.stringify(templateFromStorage);
    }

    const templateWithID = (templateID: string) => savedTemplates.find((template) => template.templateID === templateID)!;
    const hasTemplateChanged = checkIfTemplateChanged(selectedTemplate, savedTemplates);
    const isTrebbleTemplate = selectedTemplate?.type === 'trebble';
    const isDisabled = isSaving || isConfigEditing;

    const onSaveMenuClicked = async () => {
        if (isConfigEditing) {
            TrebbleMessage.error(window.getI18n(ti18n, "FINISH_EDITING_BEFORE_SAVING"));
            return;
        }
        if (isTrebbleTemplate) {
            TrebbleMessage.error(window.getI18n(ti18n, "CANNOT_SAVE_TREBBLE_TEMPLATE"));
            return;
        }
        try {
            setIsSaving(true);
            await TrebbleClientAPIHelper.getInstance().updateTemplate(
                selectedTemplate.templateID,
                selectedTemplate,
                workspaceId
            );

            setSavedTemplates((templates) => [
                ...templates.filter((t) => t.templateID !== selectedTemplate.templateID),
                selectedTemplate
            ]);
            TrebbleMessage.success(window.getI18n(ti18n, "TEMPLATE_SAVED_SUCCESS"));
        } catch (error) {
            console.error("Error saving template:", error);
            TrebbleMessage.error(window.getI18n(ti18n, "FAILED_TO_SAVE_TEMPLATE"));
        } finally {
            setIsSaving(false);
        }
    }

    const onSaveAsMenuClicked = () => {
        if (isConfigEditing) {
            TrebbleMessage.error(window.getI18n(ti18n, "FINISH_EDITING_BEFORE_SAVING"));
            return;
        }
        setIsSaveAsModalVisible(true);
        setIsSavingAs(true);
        setFormError(null);
    }

    const handleSaveAs = async () => {
        if (isConfigEditing) {
             TrebbleMessage.error(window.getI18n(ti18n, "FINISH_EDITING_BEFORE_SAVING"));
             setIsSavingAs(false);
             form.resetFields();
             return;
        }
        try {
            setIsSaving(true);
            const values = await form.validateFields();
            let newTemplate: Template = {
                ...selectedTemplate,
                templateID: `template-${Date.now()}`,
                name: values.name,
                type: 'user', // Always create as user template
            };

            const savedTemplate = await TrebbleClientAPIHelper.getInstance().createTemplate(newTemplate, workspaceId);
            
            setSavedTemplates(templates => [...templates, savedTemplate]);
            setSelectedTemplate(savedTemplate);
            setIsSavingAs(false);
            form.resetFields();
            TrebbleMessage.success(window.getI18n(ti18n, "TEMPLATE_SAVED_SUCCESS"));
        } catch (error) {
            console.error('Error saving template:', error);
            TrebbleMessage.error(window.getI18n(ti18n, "FAILED_TO_SAVE_TEMPLATE"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteOk = async () => {
        if (isConfigEditing) {
             TrebbleMessage.error(window.getI18n(ti18n, "FINISH_EDITING_BEFORE_DELETING"));
             setIsDeleteModalVisible(false);
             return;
        }
        try {
            setIsSaving(true);
            if (isTrebbleTemplate) {
                TrebbleMessage.error(window.getI18n(ti18n, "CANNOT_DELETE_TREBBLE_TEMPLATE"));
                return;
            }

            await TrebbleClientAPIHelper.getInstance().deleteTemplate(selectedTemplate.templateID, workspaceId);
            
            const remainingTemplates = savedTemplates.filter((t) => t.templateID !== selectedTemplate.templateID);
            
            if (remainingTemplates.length === 0) {
                // Create a default template if no templates remain
                const defaultTemplate = createDefaultTemplate();
                await TrebbleClientAPIHelper.getInstance().createTemplate(defaultTemplate, workspaceId);
                setSavedTemplates([defaultTemplate]);
                setSelectedTemplate(defaultTemplate);
                TrebbleMessage.success(window.getI18n(ti18n, "DEFAULT_TEMPLATE_CREATED"));
            } else {
                setSavedTemplates(remainingTemplates);
                // Select the first available template after deletion
                const nextTemplate = remainingTemplates[0];
                setSelectedTemplate(nextTemplate);
            }
            
            setIsDeleteModalVisible(false);
            TrebbleMessage.success(window.getI18n(ti18n, "TEMPLATE_DELETED_SUCCESS"));
        } catch (error) {
            console.error("Error deleting template:", error);
            TrebbleMessage.error(window.getI18n(ti18n, "FAILED_TO_DELETE_TEMPLATE"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
        TrebbleMessage.info(window.getI18n(ti18n, "DELETION_CANCELLED"));
    };

    const onResetMenuClicked = () => {
        if (isConfigEditing) {
            TrebbleMessage.error(window.getI18n(ti18n, "FINISH_EDITING_BEFORE_RESETTING"));
            return;
        }
        const originalTemplate = savedTemplates.find(t => t.templateID === selectedTemplate.templateID);
        if (originalTemplate) {
            setSelectedTemplate(originalTemplate);
            TrebbleMessage.info(window.getI18n(ti18n, "TEMPLATE_RESET_SUCCESS"));
        } else {
            TrebbleMessage.error(window.getI18n(ti18n, "TEMPLATE_RESET_FAILED"));
        }
    }

    // Whenever savedTemplates changes, check if the selected template has changed and update the memory copy
    useEffect(() => {
        if (hasTemplateChanged) {
            setSelectedTemplate(templateWithID(selectedTemplate.templateID));
        }
    }, [savedTemplates])

    return (
        <>
            <TrebbleMessage._configurator />
            <div className="mb-1">
                {window.getI18n(ti18n, "TEMPLATE_LABEL")}: {hasTemplateChanged ? window.getI18n(ti18n, "UNSAVED_CHANGES") : ""}
                {formError && <span style={{ color: '#ff4d4f', fontSize: '14px', marginLeft: '8px' }}>{window.getI18n(ti18n, "MIN_CHARACTERS_ERROR")}</span>}
            </div>
            <div className="mb-2"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}
            >
                {!isSavingAs ? (
                    <>
                        <TrebbleSelect 
                            style={{ flex: "1 1 auto" }} 
                            placeholder={window.getI18n(ti18n, "SELECT_A_TEMPLATE_PLACEHOLDER")}
                            value={selectedTemplate?.templateID} 
                            onChange={(templateID) => setSelectedTemplate(templateWithID(templateID))}
                            disabled={isDisabled}
                            className={isDisabled ? 'cursor-not-allowed' : ''}
                            options={savedTemplates.map(template => ({
                                key: template.templateID,
                                value: template.templateID,
                                label: template.name
                            }))}
                        />
                        <SaveTemplateButton 
                            className="mx-1" 
                            onSaveMenuClicked={onSaveMenuClicked} 
                            onSaveAsMenuClicked={onSaveAsMenuClicked}
                            isTrebbleTemplate={isTrebbleTemplate}
                            isSaving={isSaving}
                            isConfigEditing={isConfigEditing}
                        />
                        <MoreActionTemplateButton 
                            onResetMenuClicked={onResetMenuClicked}
                            onDeleteMenuClicked={() => setIsDeleteModalVisible(true)}
                            isTrebbleTemplate={isTrebbleTemplate}
                            className="mx-1"
                            isSaving={isSaving}
                            isConfigEditing={isConfigEditing}
                        />
                    </>
                ) : (
                    <TrebbleForm 
                        form={form}
                        onFinish={handleSaveAs}
                        autoComplete="off"
                        style={{ display: 'flex', flex: 1, gap: '8px' }}
                        onFieldsChange={(_, allFields) => {
                            const nameField = allFields[0];
                            if (!nameField.value || nameField.value.length < 3) {
                                setFormError(window.getI18n(ti18n, "MIN_CHARACTERS_ERROR"));
                                setIsFormValid(false);
                            } else {
                                setFormError(null);
                                setIsFormValid(true);
                            }
                        }}
                    >
                        <TrebbleForm.Item
                            name="name"
                            style={{ flex: 1, margin: 0 }}
                            rules={[
                                { 
                                    required: true,
                                    validator: async (_, value) => {
                                        if (!value || value.length < 3) {
                                            throw new Error(window.getI18n(ti18n, "INVALID_INPUT"));
                                        }
                                    }
                                }
                            ]}
                            validateTrigger="onChange"
                            noStyle
                        >
                            <TrebbleInput 
                                placeholder={window.getI18n(ti18n, "ENTER_NEW_TEMPLATE_NAME")}
                                autoFocus 
                                disabled={isSaving}
                                style={{ cursor: isSaving ? 'not-allowed' : 'text' }}
                            />
                        </TrebbleForm.Item>
                        <TrebbleButton 
                            type="primary" 
                            onClick={() => form.submit()} 
                            loading={isSaving}
                            disabled={!isFormValid || isSaving}
                            style={{ cursor: (!isFormValid || isSaving) ? 'not-allowed' : 'pointer' }}
                        >
                            {window.getI18n(ti18n, "SAVE")}
                        </TrebbleButton>
                        <TrebbleButton 
                            onClick={() => {
                                setIsSavingAs(false);
                                setFormError(null);
                                setIsFormValid(false);
                                form.resetFields();
                            }} 
                            disabled={isSaving}
                            style={{ cursor: isSaving ? 'not-allowed' : 'pointer' }}
                        >
                            {window.getI18n(ti18n, "CANCEL")}
                        </TrebbleButton>
                    </TrebbleForm>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <TrebbleModal
                title={window.getI18n(ti18n, "CONFIRM_DELETION_TITLE")}
                open={isDeleteModalVisible}
                onOk={handleDeleteOk}
                onCancel={handleDeleteCancel}
                okText={window.getI18n(ti18n, "DELETE")}
                cancelText={window.getI18n(ti18n, "CANCEL")}
                okButtonProps={{ 
                    danger: true, 
                    loading: isSaving,
                    disabled: isDisabled,
                    style: { cursor: isDisabled ? 'not-allowed' : 'pointer' }
                }}
                cancelButtonProps={{ 
                    disabled: isDisabled,
                    style: { cursor: isDisabled ? 'not-allowed' : 'pointer' }
                }}
            >
                <p>{window.getI18n(ti18n, "CONFIRM_DELETE_TEMPLATE", { name: selectedTemplate?.name })}</p>
            </TrebbleModal>
        </>
    )
}
